'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@material-bank/mb3-components'
import { useTranslations } from '@material-bank/i18n'
import './galleryViewer.css'
import { formatImageSource } from '@material-bank/mtag-utilities'

type GalleryViewerProps = {
  items: string[]
  activeIndex?: number | null
  onClose?: () => void
}

export function GalleryViewer({
  items,
  activeIndex,
  onClose,
}: GalleryViewerProps) {
  const t = useTranslations('product.colorways')
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set())
  const activeItemRef = useRef<HTMLElement>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.()
      }
    },
    [onClose],
  )

  const isOpen = activeIndex !== null && activeIndex !== undefined

  useEffect(() => {
    if (!isOpen) {
      return
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) {
    return null
  }

  if (typeof document === 'undefined') {
    return null
  }

  function handleImageLoad(index: number) {
    setLoadedImages((prev) => new Set(prev).add(index))

    if (index === activeIndex) {
      activeItemRef.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      })
    }
  }

  return createPortal(
    <div
      className="gallery-overlay fixed inset-0 z-50 bg-black/91"
      role="dialog"
      aria-modal="true"
      aria-label={t('galleryViewer')}
    >
      <div className="flex flex-col gap-3 h-full overflow-y-auto py-[60px]">
        {items.map((item, index) => (
          <article
            key={index}
            ref={index === activeIndex ? activeItemRef : undefined}
          >
            <div className="relative w-full overflow-hidden">
              <img
                alt={item}
                className={`w-full${
                  index === activeIndex && loadedImages.has(index)
                    ? ' border-2 border-white'
                    : ''
                }`}
                onLoad={() => handleImageLoad(index)}
                {...formatImageSource(item, 512)}
              />
            </div>
          </article>
        ))}
      </div>
      <Button
        className="close-button"
        variant="secondary"
        size="lg"
        data-haptic="light"
        data-analyticsid="product_card_photo_carousel_close_gallery_view"
        onClick={onClose}
      >
        {t('close')}
      </Button>
    </div>,
    document.body,
  )
}
